import React, { useState, useEffect, useContext } from 'react';
import {
    People,
    Add,
    Search,
    Phone,
    Email,
    Star,
    QrCode,
    Receipt,
    Close,
    Favorite,
    FavoriteBorder,
    LocationOn,
    CalendarToday,
    Payment,
    Link,
    Description
} from '@mui/icons-material';

import PaymentRequestPanel from "./PaymentRequestPanel";
import PaymentLinkPanel from "./PaymentLinkPanel";
import InvoicePanel from "./InvoicePanel";
import { fetchCustomers } from '../../services/customerService';
import { useAuth } from '../../contexts/AuthContext';

const ClientsList = ({ caissierData }) => {
    const { selectedOrganization } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showClientDetails, setShowClientDetails] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all');
    const [activeModal, setActiveModal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pge, setPage] = useState(1);
    const [lmt] = useState(10);
    const [slg, setSlg] = useState('');
    const [prevPage, setPrevPage] = useState(1);
    const [nextPage, setNextPage] = useState(1);

    // Charger les clients depuis l'API (logique du mobile)
    useEffect(() => {
        if (selectedOrganization?.id) {
            loadCustomers();
        }
    }, [selectedOrganization?.id, slg, pge]);

    // Mettre à jour la recherche avec debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setSlg(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const params = {
                pge: pge,
                lmt: lmt,
                slg: slg,
                org: selectedOrganization.id
            };
            
            const data = await fetchCustomers(params);
            
            if (data && data?.contents?.length > 0) {
                setClients(data.contents.map(transformCustomer));
                setPrevPage(data.previousPage || 1);
                setNextPage(data.nextPage || 1);
            } else {
                setClients([]);
                setPrevPage(1);
                setNextPage(1);
                setPage(1);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    // Transformer les données client de l'API au format attendu
    const transformCustomer = (customer) => ({
        id: customer.id,
        name: customer.name || '',
        phone: customer.phoneNumber ? `${customer.zip || ''}${customer.phoneNumber}` : '',
        email: customer.email || '',
        address: customer.address || '',
        totalSpent: customer.totalSpent || 0,
        transactions: customer.transactionsCount || 0,
        lastSeen: customer.lastTransactionDate ? new Date(customer.lastTransactionDate) : new Date(),
        favorite: false, // À implémenter si nécessaire
        tags: customer.tags || [],
        joinDate: customer.createdAt ? new Date(customer.createdAt) : new Date()
    });

    // Clients depuis l'API (initialisé vide, sera rempli par loadCustomers)
    const [clients, setClients] = useState([]);

    const [newClient, setNewClient] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        notes: ''
    });

    const filters = [
        { id: 'all', label: 'Tous les clients', count: clients.length },
        { id: 'favorites', label: 'Favoris', count: clients.filter(c => c.favorite).length },
        { id: 'vip', label: 'Clients VIP', count: clients.filter(c => c.tags && c.tags.includes('VIP')).length },
        { id: 'new', label: 'Nouveaux', count: clients.filter(c => c.tags && c.tags.includes('Nouveau')).length }
    ];

    // Le filtrage est maintenant géré par l'API via le paramètre slg
    // On filtre uniquement côté client pour les filtres spéciaux (favorites, vip, new)
    const filteredClients = clients.filter(client => {
        const matchesFilter = activeFilter === 'all' ||
            (activeFilter === 'favorites' && client.favorite) ||
            (activeFilter === 'vip' && client.tags && client.tags.includes('VIP')) ||
            (activeFilter === 'new' && client.tags && client.tags.includes('Nouveau'));

        return matchesFilter;
    });

    const addClient = () => {
        if (!newClient.name || !newClient.phone) return;

        const client = {
            id: clients.length + 1,
            ...newClient,
            totalSpent: 0,
            transactions: 0,
            lastSeen: new Date(),
            favorite: false,
            tags: ['Nouveau'],
            joinDate: new Date()
        };

        setClients([client, ...clients]);
        setNewClient({ name: '', phone: '', email: '', address: '', notes: '' });
        setShowAddForm(false);
        setSelectedClient(client);
        setShowClientDetails(true);
    };

    const toggleFavorite = (clientId) => {
        setClients(clients.map(client =>
            client.id === clientId ? { ...client, favorite: !client.favorite } : client
        ));

        if (selectedClient && selectedClient.id === clientId) {
            setSelectedClient(prev => ({ ...prev, favorite: !prev.favorite }));
        }
    };

    const handleClientClick = (client) => {
        setSelectedClient(client);
        setShowClientDetails(true);
    };

    const handleActionClick = (action, client) => {
        setSelectedClient(client);
        setActiveModal(action);
        setShowClientDetails(false);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const closeClientDetails = () => {
        setShowClientDetails(false);
        setSelectedClient(null);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const formatJoinDate = (date) => {
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    };

    // Composant ModalWrapper réutilisable
    const ModalWrapper = ({ children, title, subtitle, icon: Icon, onClose, size = "lg" }) => (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-lg z-50 flex items-center justify-center p-4">
            <div className={`w-full ${
                size === "sm" ? "max-w-md" :
                    size === "md" ? "max-w-2xl" :
                        size === "lg" ? "max-w-4xl" :
                            "max-w-6xl"
            } h-[95vh] flex flex-col`}>
                <div className="bg-white rounded-3xl shadow-2xl flex flex-col h-full overflow-hidden">
                    {/* En-tête fixe */}
                    <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                        <div className="flex items-center space-x-3">
                            {Icon && <Icon className="text-2xl text-amber-600" />}
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
                                {subtitle && <p className="text-gray-600">{subtitle}</p>}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-2xl transition-all duration-300"
                        >
                            <Close className="text-2xl" />
                        </button>
                    </div>

                    {/* Contenu scrollable */}
                    <div className="flex-1 overflow-hidden">
                        <div className="h-full overflow-y-auto">
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Composant ClientDetailsModal
    const ClientDetailsModal = ({ client, onClose, onActionClick }) => (
        <ModalWrapper
            title={`Détails de ${client.name}`}
            subtitle="Informations complètes du client"
            icon={People}
            onClose={onClose}
            size="md"
        >
            <div className="p-6">
                {/* En-tête du client */}
                <div className="text-center mb-8">
                    <div className="relative inline-block">
                        <div className="w-32 h-32 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
                            <span className="text-white font-bold text-4xl">
                                {client.name.split(' ').map(n => n[0]).join('')}
                            </span>
                        </div>
                        <button
                            onClick={() => toggleFavorite(client.id)}
                            className="absolute top-2 right-2 p-3 bg-white rounded-full shadow-lg hover:scale-110 transition-transform"
                        >
                            {client.favorite ? (
                                <Favorite className="text-amber-500 text-xl" />
                            ) : (
                                <FavoriteBorder className="text-gray-400 text-xl" />
                            )}
                        </button>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{client.name}</h3>
                    <div className="text-lg text-gray-600 mb-1">{client.phone}</div>
                    {client.email && (
                        <div className="text-gray-500">{client.email}</div>
                    )}
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-4 border border-amber-200 text-center">
                        <div className="text-2xl font-bold text-amber-600 mb-1">
                            {formatCurrency(client.totalSpent)}
                        </div>
                        <div className="text-sm text-amber-700 font-medium">Total dépensé</div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-4 border border-blue-200 text-center">
                        <div className="text-2xl font-bold text-blue-600 mb-1">
                            {client.transactions}
                        </div>
                        <div className="text-sm text-blue-700 font-medium">Transactions</div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-200 text-center">
                        <div className="text-2xl font-bold text-green-600 mb-1">
                            Actif
                        </div>
                        <div className="text-sm text-green-700 font-medium">Statut</div>
                    </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6 justify-center">
                    {client.tags.map((tag, index) => (
                        <span
                            key={index}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                                tag === 'VIP' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                                    tag === 'Fidèle' ? 'bg-green-100 text-green-700 border border-green-200' :
                                        tag === 'Nouveau' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                                            'bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                        >
                            {tag}
                        </span>
                    ))}
                </div>

                {/* Informations détaillées */}
                <div className="space-y-4 mb-8">
                    {client.address && (
                        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-2xl">
                            <LocationOn className="text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-semibold text-gray-700 mb-1">Adresse</div>
                                <div className="text-gray-600">{client.address}</div>
                            </div>
                        </div>
                    )}
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-2xl">
                        <CalendarToday className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-semibold text-gray-700 mb-1">Membre depuis</div>
                            <div className="text-gray-600">{formatJoinDate(client.joinDate)}</div>
                        </div>
                    </div>
                    <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-2xl">
                        <CalendarToday className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-semibold text-gray-700 mb-1">Dernière visite</div>
                            <div className="text-gray-600">{formatDate(client.lastSeen)}</div>
                        </div>
                    </div>
                </div>

                {/* Boutons d'action rapide */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                    <button
                        onClick={() => onActionClick('payment', client)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-4 rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg"
                    >
                        <Payment />
                        <span>Demander Paiement</span>
                    </button>
                    <button
                        onClick={() => onActionClick('link', client)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-4 rounded-2xl font-semibold hover:from-purple-600 hover:to-purple-700 transition-all duration-300"
                    >
                        <Link />
                        <span>Lien de Paiement</span>
                    </button>
                    {/* Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
                    <button
                        onClick={() => onActionClick('invoice', client)}
                        className="flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-4 rounded-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300"
                    >
                        <Description />
                        <span>Créer Facture</span>
                    </button>
                    */}
                </div>

                {/* Actions supplémentaires */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button className="flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-4 rounded-2xl font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all duration-300">
                        <QrCode />
                        <span>Générer QR Code</span>
                    </button>
                    <button className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-4 rounded-2xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-300">
                        <Receipt />
                        <span>Historique Transactions</span>
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg">
                                <People className="text-white text-3xl" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Gestion des Clients</h1>
                                <p className="text-gray-600 text-lg">
                                    {clients.length} clients enregistrés • {clients.filter(c => c.favorite).length} favoris
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowAddForm(true)}
                            className="flex items-center space-x-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <Add className="text-xl" />
                            <span className="text-lg">Nouveau Client</span>
                        </button>
                    </div>
                </div>

                {/* Liste des Clients - Pleine largeur maintenant */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                    {/* Barre de Recherche et Filtres */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-6">
                        <div className="flex-1 max-w-md">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                                <input
                                    type="text"
                                    placeholder="Rechercher un client par nom, téléphone ou email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-2xl text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200 text-lg"
                                />
                            </div>
                        </div>

                        <div className="flex space-x-2 overflow-x-auto">
                            {filters.map(filter => (
                                <button
                                    key={filter.id}
                                    onClick={() => setActiveFilter(filter.id)}
                                    className={`flex items-center space-x-2 px-4 py-3 rounded-2xl font-semibold transition-all duration-200 whitespace-nowrap ${
                                        activeFilter === filter.id
                                            ? 'bg-amber-500 text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    <span>{filter.label}</span>
                                    <span className={`px-2 py-1 rounded-full text-xs ${
                                        activeFilter === filter.id
                                            ? 'bg-white text-amber-600'
                                            : 'bg-gray-300 text-gray-700'
                                    }`}>
                                        {filter.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* En-tête du tableau */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl mb-4 text-sm font-semibold text-gray-700">
                        <div className="col-span-5">Client</div>
                        <div className="col-span-2 text-center">Transactions</div>
                        <div className="col-span-3 text-right">Total Dépensé</div>
                        <div className="col-span-2 text-right">Dernière Visite</div>
                    </div>

                    {/* Liste des Clients */}
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {filteredClients.map((client) => (
                            <div
                                key={client.id}
                                className={`grid grid-cols-12 gap-4 p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer hover:shadow-lg ${
                                    selectedClient?.id === client.id
                                        ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50'
                                        : 'border-gray-100 hover:border-amber-200'
                                }`}
                                onClick={() => handleClientClick(client)}
                            >
                                {/* Info Client */}
                                <div className="col-span-5 flex items-center space-x-4">
                                    <div className="relative">
                                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-md">
                                            <span className="text-white font-bold text-lg">
                                                {client.name.split(' ').map(n => n[0]).join('')}
                                            </span>
                                        </div>
                                        {client.favorite && (
                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                                                <Star className="text-white text-xs" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="font-semibold text-gray-900 text-lg truncate">
                                                {client.name}
                                            </h3>
                                        </div>
                                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                                            <div className="flex items-center space-x-1">
                                                <Phone className="text-xs" />
                                                <span>{client.phone}</span>
                                            </div>
                                            {client.email && (
                                                <div className="flex items-center space-x-1">
                                                    <Email className="text-xs" />
                                                    <span className="truncate max-w-[120px]">{client.email}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex space-x-2 mt-2">
                                            {client.tags.map((tag, index) => (
                                                <span
                                                    key={index}
                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        tag === 'VIP' ? 'bg-purple-100 text-purple-700' :
                                                            tag === 'Fidèle' ? 'bg-green-100 text-green-700' :
                                                                tag === 'Nouveau' ? 'bg-blue-100 text-blue-700' :
                                                                    'bg-gray-100 text-gray-700'
                                                    }`}
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Transactions */}
                                <div className="col-span-2 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-gray-900">{client.transactions}</div>
                                        <div className="text-xs text-gray-600">transactions</div>
                                    </div>
                                </div>

                                {/* Total Dépensé */}
                                <div className="col-span-3 flex items-center justify-end">
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-amber-600">
                                            {formatCurrency(client.totalSpent)}
                                        </div>
                                        <div className="text-xs text-gray-600">dépensé au total</div>
                                    </div>
                                </div>

                                {/* Dernière Visite */}
                                <div className="col-span-2 flex items-center justify-end">
                                    <div className="text-right">
                                        <div className="text-sm font-semibold text-gray-900">
                                            {formatDate(client.lastSeen)}
                                        </div>
                                        <div className="text-xs text-gray-600">dernière visite</div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {filteredClients.length === 0 && (
                            <div className="text-center py-12">
                                <div className="text-6xl text-gray-300 mb-4">👥</div>
                                <h3 className="text-lg font-semibold text-gray-500 mb-2">
                                    Aucun client trouvé
                                </h3>
                                <p className="text-gray-400">
                                    Aucun client ne correspond à vos critères de recherche
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal d'ajout de client */}
            {showAddForm && (
                <ModalWrapper
                    title="Nouveau Client"
                    subtitle="Ajoutez un nouveau client à votre base"
                    icon={Add}
                    onClose={() => setShowAddForm(false)}
                >
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Nom complet *
                                </label>
                                <input
                                    type="text"
                                    value={newClient.name}
                                    onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                                    className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200"
                                    placeholder="Jean Dupont"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Téléphone *
                                </label>
                                <input
                                    type="tel"
                                    value={newClient.phone}
                                    onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                                    className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200"
                                    placeholder="+243 81 234 5678"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email (optionnel)
                                </label>
                                <input
                                    type="email"
                                    value={newClient.email}
                                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                                    className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200"
                                    placeholder="client@email.com"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Adresse (optionnel)
                                </label>
                                <input
                                    type="text"
                                    value={newClient.address}
                                    onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                                    className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200"
                                    placeholder="123 Avenue de la Paix, Kinshasa"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Notes (optionnel)
                            </label>
                            <textarea
                                value={newClient.notes}
                                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                                rows="3"
                                className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 focus:ring-2 focus:ring-amber-500 focus:outline-none transition-all duration-200 resize-none"
                                placeholder="Informations supplémentaires sur le client..."
                            />
                        </div>
                    </div>

                    <div className="flex-shrink-0 flex space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={() => setShowAddForm(false)}
                            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all duration-200"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={addClient}
                            disabled={!newClient.name || !newClient.phone}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-2xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Ajouter le Client
                        </button>
                    </div>
                </ModalWrapper>
            )}

            {/* Modal des détails du client */}
            {showClientDetails && selectedClient && (
                <ClientDetailsModal
                    client={selectedClient}
                    onClose={closeClientDetails}
                    onActionClick={handleActionClick}
                />
            )}

            {/* Modaux pour les actions */}
            {activeModal === 'payment' && (
                <ModalWrapper
                    title="Demander un Paiement"
                    subtitle={`Envoyer une demande de paiement à ${selectedClient?.name}`}
                    icon={Payment}
                    onClose={closeModal}
                >
                    <PaymentRequestPanel
                        caissierData={caissierData}
                        selectedClient={selectedClient}
                        onClose={closeModal}
                    />
                </ModalWrapper>
            )}

            {activeModal === 'link' && (
                <ModalWrapper
                    title="Lien de Paiement"
                    subtitle={`Créer un lien de paiement pour ${selectedClient?.name}`}
                    icon={Link}
                    onClose={closeModal}
                >
                    <PaymentLinkPanel
                        caissierData={caissierData}
                        selectedClient={selectedClient}
                        onClose={closeModal}
                    />
                </ModalWrapper>
            )}

            {/* Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
            {activeModal === 'invoice' && (
                <ModalWrapper
                    title="Créer une Facture"
                    subtitle={`Générer une facture pour ${selectedClient?.name}`}
                    icon={Description}
                    onClose={closeModal}
                >
                    <InvoicePanel
                        selectedClient={selectedClient}
                        onClose={closeModal}
                    />
                </ModalWrapper>
            )}
            */}
        </div>
    );
};

export default ClientsList;